import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil } from 'rxjs/operators';
import { IdTitleSelector } from '@core/models/ui.models';
import { MatSelectionList } from '@angular/material/list';

const LIMIT_FOR_VIRTUAL_SCROLL_TOGGLE = 10;
/** пауза на набор перед запросом к серверу */
const SERVER_SEARCH_DEBOUNCE_MS = 300;

/**
 * @description меню с выпадающим списком элементов для встраивания в шапку таблицы
 * @param selected выбранный элемент списка
 * @param options список элементов для выбора
 * @param searchFn поиск на сервере: список не выкачивают целиком, options тогда — только подписи выбранного
 * @param filterChange эмиттер изменения выбранного элемента
 */

@Component({
    selector: 'nekta-table-filter',
    templateUrl: './nekta-table-filter.component.html',
    styleUrls: ['./nekta-table-filter.component.less']
})
export class NektaTableFilterComponent<T = any, S = any> implements OnInit, OnChanges, OnDestroy {
    @Input() filterType: 'select' | 'text' = 'select';
    @Input() selected: T | null = null;
    @Input() options: IdTitleSelector[] = [];
    @Input() devices: any[] = [];
    @Input() isCollectionTimeshiftComponent = false;
    @Input() multiple = false;
    @Input() searchFn: ((query: string) => Observable<IdTitleSelector[]>) | undefined;

    @Output() filterChange: EventEmitter<any> = new EventEmitter<any>();

    filteredOptions: IdTitleSelector<T, S>[] = [];
    searchString: string | null = null;
    selectedOption: T | null = null;
    selectedValues: T[] = [];

    textValue;

    withVirtualScroll = false;
    /** идёт запрос вариантов на сервер */
    isLoading = false;

    @ViewChild('matSelectionList') matSelectionList: MatSelectionList;

    private readonly serverSearch$ = new Subject<string>();
    private readonly destroyed$ = new ReplaySubject<boolean>(1);
    /** пункты, уже приходившие с сервера: выбранное остаётся в списке, даже когда поиск его не вернул */
    private readonly knownOptions = new Map<any, IdTitleSelector>();
    private isServerListLoaded = false;

    ngOnInit(): void {
        if (this.filterType === 'text') {
            this.textValue = this.selected || '';
        } else {
            // при серверном поиске пунктов может быть сколько угодно — поле поиска нужно всегда
            this.withVirtualScroll = !!this.searchFn || this.options.length > LIMIT_FOR_VIRTUAL_SCROLL_TOGGLE;
            this.filteredOptions = this.options.slice(0);

            this.initSelection();
        }
        if (this.searchFn) {
            this.serverSearch$
                .pipe(
                    debounceTime(SERVER_SEARCH_DEBOUNCE_MS),
                    distinctUntilChanged(),
                    switchMap((query) => {
                        // флаг ставим уже после паузы на набор: отсеянный distinctUntilChanged запрос не оставит его висеть
                        this.isLoading = true;
                        return this.searchFn(query).pipe(catchError(() => of([])));
                    }),
                    takeUntil(this.destroyed$)
                )
                .subscribe((found) => this.setServerOptions(found ?? []));
        }
    }

    ngOnDestroy() {
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.selected && !changes.selected.firstChange) {
            if (this.filterType === 'text') {
                this.textValue = this.selected || '';
            } else {
                this.initSelection();
            }
        }
    }

    initSelection() {
        if (this.multiple) {
            const sel = this.selected;
            if (Array.isArray(sel)) {
                this.selectedValues = sel.filter(v => v !== null);
            } else if (sel !== null && sel !== undefined) {
                this.selectedValues = [sel as T];
            } else {
                this.selectedValues = [];
            }
        } else {
            this.selectedOption = (this.selected !== null && this.selected !== undefined) ? this.selected : null;
        }
    }

    /** первый список с сервера — при первом открытии меню, а не на каждую отрисовку таблицы */
    onMenuOpened() {
        if (this.searchFn && !this.isServerListLoaded) {
            this.isServerListLoaded = true;
            this.serverSearch$.next('');
        }
    }

    onFilterChange(v: T | null) {
        this.selectedOption = v;
        this.filterChange.emit(v);
    }

    onSearch(str: string | null) {
        this.searchString = str?.toLowerCase();
        if (this.searchFn) {
            this.serverSearch$.next((str ?? '').trim());
            return;
        }
        if (!str) {
            this.filteredOptions = this.options.slice(0);
        } else {
            this.filteredOptions = this.options.filter((w) => w.title?.toLowerCase().includes(this.searchString));
        }
    }

    trackByFn(index: number, item: IdTitleSelector) {
        return item?.id;
    }

    onMultiSelectionChange() {
        if (this.selectedValues.includes(null as any)) {
            this.selectedValues = [];
            if (this.matSelectionList) {
                this.matSelectionList.deselectAll();
            }
            this.filterChange.emit(null);
        } else {
            const values = this.selectedValues.filter(v => v !== null);
            this.selectedValues = values;
            this.filterChange.emit(values.length ? values : null);
        }
    }

    onTextInput(value: string) {
        this.textValue = value;
    }

    onTextSearch() {
        this.filterChange.emit(this.textValue || null);
    }

    onTextClear() {
        this.textValue = '';
        this.filterChange.emit(null);
    }

    /**
     * найденное сервером, а выбранное, которого среди найденного нет, — сверху: список выбора
     * помнит только отрисованные пункты, и без этого выбор нового шлюза терял бы прежние
     */
    private setServerOptions(found: IdTitleSelector[]) {
        this.isLoading = false;
        found.forEach((option) => this.knownOptions.set(option.id, option));
        const selectedIds: any[] = this.multiple ? this.selectedValues : this.selectedOption !== null ? [this.selectedOption] : [];
        const pinned = selectedIds
            .filter((id) => !found.some((option) => option.id === id))
            .map((id) => this.knownOptions.get(id) ?? this.options.find((option) => option.id === id) ?? { id, title: String(id) });
        this.filteredOptions = [...pinned, ...found] as IdTitleSelector<T, S>[];
    }
}
